import { Flex } from "src/components/Flex.tsx";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import {
  ButtonV2,
  type ButtonV2Skin,
  type ButtonV2Variant,
} from "src/components/ButtonV2/ButtonV2.tsx";
import { CrossCircleIcon } from "src/lib/ui-icon-v2";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { Text } from "src/components/Text/Text.tsx";
import { PaginationV2 } from "src/components/PaginationV2/PaginationV2.tsx";
import { useTranslation } from "react-i18next";
import { useGridSelector } from "@mui/x-data-grid";
import {
  gridFilterModelSelector as realGridFilterModelSelector,
  gridPageCountSelector as realGridPageCountSelector,
  type GridPaginationModel,
  gridPaginationModelSelector as realGridPaginationModelSelector,
  gridPaginationRowCountSelector as realGridPaginationRowCountSelector,
} from "@mui/x-data-grid-premium";
import React, {
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
} from "react";
import type { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { ThemeProvider } from "@mui/material";
import { Scrollable } from "src/components/Scrollable/Scrollable.tsx";
import * as styles from "./DataGridToolbar.style.ts";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { observer } from "mobx-react-lite";

export type ToolbarProps = {
  hideCreateNewRecordButton?: boolean;
  hideCsvButton?: boolean;
  filterMode?: "server" | "client";
  onExportCsv?: (e: MouseEvent<HTMLButtonElement>) => void;
  onCreateNewRecord?: (e: MouseEvent<HTMLButtonElement>) => void;
  hidePagination?: boolean;
  hideRefreshButton?: boolean;
  disableRefreshButton?: boolean;
  onRefresh?: (e: MouseEvent<HTMLButtonElement>) => void;
  startAdditionalElements?: ReactNode[];
  disablePagination?: boolean;
  disableCreateNewRecordButton?: boolean;
  createNewRecordText?: string;
  createNewRecordType?: HTMLButtonElement["type"];
  createNewRecordSkin?: ButtonV2Skin;
  createNewRecordVariant?: ButtonV2Variant;
  apiRef: MutableRefObject<GridApiPremium>;
};

const dumpGridPageCountSelector = () => 0;
const dumpGridPaginationModelSelector = () => ({
  page: 0,
  pageSize: 0,
});

const dumpGridFilterModelSelector = () => ({
  items: [],
});

const dumpGridPaginationRowCountSelector = () => 0;

export const DataGridToolbar = observer(
  ({
    onRefresh,
    apiRef,
    hideCsvButton,
    onCreateNewRecord,
    hideCreateNewRecordButton,
    disableCreateNewRecordButton,
    disableRefreshButton,
    onExportCsv,
    createNewRecordText,
    filterMode,
    createNewRecordSkin = "success",
    createNewRecordVariant = "filled",
    createNewRecordType,
    startAdditionalElements,
    disablePagination,
    hidePagination,
    hideRefreshButton,
  }: ToolbarProps) => {
    const apiRefInitialized = !!apiRef.current.instanceId;

    const gridPageCountSelector = apiRefInitialized
      ? realGridPageCountSelector
      : dumpGridPageCountSelector;
    const gridPaginationModelSelector = apiRefInitialized
      ? realGridPaginationModelSelector
      : dumpGridPaginationModelSelector;
    const gridPaginationRowCountSelector = apiRefInitialized
      ? realGridPaginationRowCountSelector
      : dumpGridPaginationRowCountSelector;

    const gridFilterModelSelector = apiRefInitialized
      ? realGridFilterModelSelector
      : dumpGridFilterModelSelector;

    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

    const paginationModel: GridPaginationModel = useGridSelector(
      apiRef,
      gridPaginationModelSelector,
    );

    const store = useDataGridStore();

    // row count can be undefined
    const unsafeRowCount = useGridSelector(
      apiRef,
      gridPaginationRowCountSelector,
    ) as number | undefined;
    // idea: should we hide pagination until row count is defined?
    const rowCount = unsafeRowCount ?? 0;
    const lastPage = React.useMemo(
      () => Math.max(0, pageCount - 1),
      [pageCount],
    );

    const computedPage = React.useMemo(() => {
      if (rowCount === -1) {
        return paginationModel.page;
      }
      return paginationModel.page <= lastPage ? paginationModel.page : lastPage;
    }, [lastPage, paginationModel.page, rowCount]);

    const handlePageChange = React.useCallback(
      (page: number) => {
        apiRef.current.setPage(page);
      },
      [apiRef],
    );

    const fromPage = Math.min(
      computedPage * paginationModel.pageSize + 1,
      rowCount,
    );
    const toPage = Math.min(
      (computedPage + 1) * paginationModel.pageSize,
      rowCount,
    );

    const { t } = useTranslation();
    const theme = useNonConflictingTheme();

    // const toggleFilterPanel = () => {
    //   if (apiRef.current.state.preferencePanel.open) {
    //     apiRef.current.hideFilterPanel();
    //   } else {
    //     apiRef.current.showFilterPanel();
    //   }
    // };

    const resetFilters = () => {
      apiRef.current.setFilterModel({
        items: [],
      });
      store.setAppliedFilterModel({
        items: [],
      });
    };

    const filterCount =
      filterMode === "server"
        ? store.appliedFilterModel.items.length
        : filterModel.items.length;

    const hasAppliedFilters = filterCount > 0;

    return (
      <ThemeProvider theme={theme}>
        <Scrollable disableVertical css={styles.root}>
          <Flex
            justifyContent="space-between"
            gap="10px"
            flexShrink={0}
            css={styles.inner}
          >
            <Flex gap="10px" alignItems="center" flexShrink={0}>
              <IconButtonV2
                variant="outlined"
                size="sm"
                style={{
                  display: hideRefreshButton ? "none" : undefined,
                }}
                skin="info"
                onClick={onRefresh}
                disabled={disableRefreshButton}
              >
                <i className="icon-15-update" />
              </IconButtonV2>

              <ButtonV2
                size="sm"
                css={styles.createNewRecordButton}
                style={{
                  display: hideCreateNewRecordButton ? "none" : undefined,
                }}
                startAdornment={
                  <CrossCircleIcon css={styles.createNewRecordButtonIcon} />
                }
                disabled={disableCreateNewRecordButton}
                variant={createNewRecordVariant}
                skin={createNewRecordSkin}
                type={createNewRecordType}
                onClick={onCreateNewRecord}
              >
                {createNewRecordText ?? "Create new record"}
              </ButtonV2>

              <ButtonV2
                size="sm"
                variant="outlined"
                style={{
                  display: hideCsvButton ? "none" : undefined,
                }}
                onClick={onExportCsv}
                startAdornment={<span className={"icon-15-import"} />}
              >
                {t("csv", { ns: TranslationNamespaceEnum.Common })}
              </ButtonV2>

              {startAdditionalElements}
              {/*{filterModel.items.length > 0 && (*/}
              {/*  <ButtonV2*/}
              {/*    onClick={toggleFilterPanel}*/}
              {/*    variant={"outlined"}*/}
              {/*    skin={"info"}*/}
              {/*    style={{*/}
              {/*      flexShrink: 0,*/}
              {/*    }}*/}
              {/*    count={filterModel.items.length}*/}
              {/*    startAdornment={<span className={"icon-15-filter"} />}*/}
              {/*    size={"sm"}*/}
              {/*  >*/}
              {/*    {t("filter", {*/}
              {/*      ns: TranslationNamespaceEnum.DataGridComponent,*/}
              {/*    })}*/}
              {/*  </ButtonV2>*/}
              {/*)}*/}

              <ButtonV2
                onClick={resetFilters}
                variant={"outlined"}
                skin={"danger"}
                style={{
                  flexShrink: 0,
                  display: !hasAppliedFilters ? "none" : undefined,
                }}
                count={filterCount}
                startAdornment={<span className={"icon-15-filter"} />}
                size={"sm"}
              >
                {t("resetFilters", {
                  ns: TranslationNamespaceEnum.DataGridComponent,
                })}
              </ButtonV2>
            </Flex>

            {!hidePagination && (
              <Flex gap="10px" alignItems="center" flexShrink={0}>
                <Flex alignItems={"center"}>
                  <Text css={styles.showingRecordText}>
                    {t("paginationMetaText", {
                      total: rowCount,
                      from: fromPage,
                      to: toPage,
                      count: pageCount,
                      ns: TranslationNamespaceEnum.DataGridComponent,
                    })}
                  </Text>
                  <PaginationV2
                    disabled={disablePagination}
                    count={pageCount}
                    page={Math.min(computedPage + 1, pageCount)}
                    onChange={(page) => handlePageChange(page - 1)}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        </Scrollable>
      </ThemeProvider>
    );
  },
);
